import { LoaderFunction, redirect, useLoaderData } from "react-router-dom";

import { ReferralRewardsContainer } from "@/modules/referralRewards";
import { queryClient } from "@/pages/Root";
import { loadIfFlag } from "@/services/Flags";
import { get } from "api";
import { waitObj } from "toolbox/Promise";

const load = async () => {
  const platform = await queryClient.fetchQuery(["/platform/current"], () =>
    get("/platform/current")
  );

  const userInfo = await queryClient.fetchQuery(["/user"], () => get("/user"), {
    staleTime: 1000 * 60 * 2,
  });

  const referralRewardForPlatformLocation = queryClient
    .fetchQuery(
      [
        "/incentives/{platformLocationId}/available-referral-reward",
        userInfo?.platformLocation?.id,
      ],
      () =>
        get("/incentives/{platformLocationId}/available-referral-reward", {
          platformLocationId: userInfo?.platformLocation?.id ?? "",
        })
    )
    .catch(() => null);

  const referralHistoryForPerson = queryClient
    .fetchQuery(
      [
        "/incentives/{platformId}/referral-history/{personId}",
        platform?.id,
        userInfo?.id,
      ],
      () =>
        get("/incentives/{platformId}/referral-history/{personId}", {
          platformId: platform?.id ?? "",
          personId: userInfo?.id ?? "",
        })
    )
    .catch(() => null);

  const referralAchievementsForPerson = queryClient
    .fetchQuery(["/referral-achievements", platform?.id], () =>
      get("/incentives/{platformId}/referral-achievements/{personId}", {
        platformId: platform?.id ?? "",
        personId: userInfo?.id ?? "",
      })
    )
    .catch(() => null);

  return waitObj({
    userInfo,
    platform,
    referralReward: referralRewardForPlatformLocation,
    referralHistoryForPerson,
    referralAchievementsForPerson,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const referralRewardsPageLoader: LoaderFunction = loadIfFlag(
  "referral-rewards-phase-2"
)(async () => {
  const data = await load();

  // Redirect to home if rewards are disabled
  if (!data.platform?.platformSettings?.rewardsEnabled) {
    return redirect("/home");
  }

  return data;
});

export default function ReferralRewardsPage() {
  const {
    platform,
    referralReward,
    referralHistoryForPerson,
    referralAchievementsForPerson,
  } = useLoaderData() as LoaderData;

  return (
    <ReferralRewardsContainer
      platform={platform!}
      referralReward={referralReward!}
      referralHistoryForPerson={referralHistoryForPerson!}
      achievementProgress={referralAchievementsForPerson!}
    />
  );
}
